import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ConfirmModalProps {
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" = red confirm button (default), "warning" = yellow */
  variant?: "danger" | "warning";
  /** "sm" = 384px (default), "lg" = 720px */
  size?: "sm" | "lg";
  loading?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmModal({
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "danger",
  size = "sm",
  loading = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  const confirmCls =
    variant === "warning"
      ? "bg-yellow-500 hover:bg-yellow-600 text-black"
      : "bg-red-600 hover:bg-red-700 text-white";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className={`relative w-full mx-4 bg-[#0f1117] border border-zinc-800 rounded-2xl shadow-2xl p-6 ${size === "lg" ? "max-w-2xl" : "max-w-sm"}`}>
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 p-1 rounded-lg text-zinc-600 hover:text-zinc-300 hover:bg-zinc-800 transition"
        >
          <X size={14} />
        </button>

        <h2 className="text-white text-sm font-bold mb-3 pr-6">{title}</h2>
        <div className="text-zinc-400 text-xs leading-relaxed mb-6">{body}</div>

        <div className="flex gap-2 justify-end">
          {cancelLabel && (
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-zinc-800 text-zinc-300 hover:text-white hover:bg-zinc-700 transition disabled:opacity-40"
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={onConfirm}
            disabled={loading || confirmDisabled}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition disabled:opacity-40 disabled:cursor-not-allowed ${confirmCls}`}
          >
            {loading ? "Please wait…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
